import React from "react";

interface PlayfulLoaderProps {
  size?: "sm" | "md" | "lg";
  variant?: "toys" | "stars" | "rainbow" | "bounce" | "spinner";
  message?: string;
  fullScreen?: boolean;
}

export const PlayfulLoader: React.FC<PlayfulLoaderProps> = ({
  size = "md",
  variant = "toys",
  message,
  fullScreen = false,
}) => {
  const sizeClasses = {
    sm: "text-2xl",
    md: "text-4xl",
    lg: "text-6xl",
  };

  const containerClasses = fullScreen
    ? "min-h-screen bg-gradient-to-br from-purple-50 via-pink-50 to-blue-50 flex items-center justify-center"
    : "flex items-center justify-center py-8";

  const renderLoader = () => {
    switch (variant) {
      case "toys":
        return (
          <div className="flex items-center justify-center gap-2">
            <span
              className={`${sizeClasses[size]} animate-bounce`}
              style={{ animationDelay: "0s" }}
            >
              🧸
            </span>
            <span
              className={`${sizeClasses[size]} animate-bounce`}
              style={{ animationDelay: "0.2s" }}
            >
              🎈
            </span>
            <span
              className={`${sizeClasses[size]} animate-bounce`}
              style={{ animationDelay: "0.4s" }}
            >
              🎁
            </span>
            <span
              className={`${sizeClasses[size]} animate-bounce`}
              style={{ animationDelay: "0.6s" }}
            >
              🎮
            </span>
            <span
              className={`${sizeClasses[size]} animate-bounce`}
              style={{ animationDelay: "0.8s" }}
            >
              🎨
            </span>
          </div>
        );

      case "stars":
        return (
          <div className="flex items-center justify-center gap-2">
            <span
              className={`${sizeClasses[size]} animate-pulse`}
              style={{ animationDelay: "0s" }}
            >
              ⭐
            </span>
            <span
              className={`${sizeClasses[size]} animate-pulse`}
              style={{ animationDelay: "0.2s" }}
            >
              ✨
            </span>
            <span
              className={`${sizeClasses[size]} animate-pulse`}
              style={{ animationDelay: "0.4s" }}
            >
              🌟
            </span>
            <span
              className={`${sizeClasses[size]} animate-pulse`}
              style={{ animationDelay: "0.6s" }}
            >
              💫
            </span>
          </div>
        );

      case "rainbow":
        return (
          <div className="relative">
            <div className="flex items-center justify-center gap-1">
              <span
                className={`${sizeClasses[size]} animate-spin-slow`}
                style={{ animationDelay: "0s" }}
              >
                🌈
              </span>
              <span
                className={`${sizeClasses[size]} animate-spin-slow`}
                style={{ animationDelay: "0.3s" }}
              >
                🎨
              </span>
              <span
                className={`${sizeClasses[size]} animate-spin-slow`}
                style={{ animationDelay: "0.6s" }}
              >
                🎭
              </span>
            </div>
          </div>
        );

      case "bounce":
        return (
          <div className="flex items-center justify-center gap-3">
            <div
              className={`w-4 h-4 bg-purple-500 rounded-full animate-bounce-gentle`}
              style={{ animationDelay: "0s" }}
            ></div>
            <div
              className={`w-4 h-4 bg-pink-500 rounded-full animate-bounce-gentle`}
              style={{ animationDelay: "0.2s" }}
            ></div>
            <div
              className={`w-4 h-4 bg-blue-500 rounded-full animate-bounce-gentle`}
              style={{ animationDelay: "0.4s" }}
            ></div>
            <div
              className={`w-4 h-4 bg-yellow-500 rounded-full animate-bounce-gentle`}
              style={{ animationDelay: "0.6s" }}
            ></div>
            <div
              className={`w-4 h-4 bg-green-500 rounded-full animate-bounce-gentle`}
              style={{ animationDelay: "0.8s" }}
            ></div>
          </div>
        );

      case "spinner":
      default:
        return (
          <div className="relative">
            <div
              className={`border-4 border-purple-200 border-t-purple-600 rounded-full animate-spin-slow ${
                size === "sm"
                  ? "w-8 h-8"
                  : size === "md"
                  ? "w-12 h-12"
                  : "w-16 h-16"
              }`}
            ></div>
            <div className="absolute inset-0 flex items-center justify-center">
              <span className="text-2xl animate-bounce-gentle">🎁</span>
            </div>
          </div>
        );
    }
  };

  return (
    <div className={containerClasses}>
      <div className="text-center">
        {renderLoader()}
        {message && (
          <p className="mt-6 text-lg font-semibold text-gray-700 animate-pulse">
            {message}
          </p>
        )}
      </div>
    </div>
  );
};

export default PlayfulLoader;
